import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface BillSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  onPrint: () => void;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: string;
  amountPaid?: number;
  change?: number;
}

export const BillSummaryDialog: React.FC<BillSummaryDialogProps> = ({
  open,
  onClose,
  onPrint,
  items,
  subtotal,
  tax,
  total,
  paymentMethod,
  amountPaid,
  change,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          minWidth: 480,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1E8078",
          py: 3,
          px: 3,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Icon sx={{ fontSize: 28 }}>receipt_long</Icon>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Order Summary
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Order Items */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: "#1E8078",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>shopping_cart</Icon>
              Order Items
            </Typography>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha("#1E8078", 0.03),
                borderRadius: 3,
                border: `1px solid ${alpha("#1E8078", 0.1)}`,
              }}
            >
              <Stack spacing={2} sx={{ p: 2 }}>
                {items.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {index > 0 && (
                      <Divider sx={{ borderColor: alpha("#1E8078", 0.1) }} />
                    )}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 500 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                        >
                          {item.quantity} x BHD {item.price.toFixed(3)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        BHD {(item.price * item.quantity).toFixed(3)}
                      </Typography>
                    </Stack>
                  </React.Fragment>
                ))}
              </Stack>
            </Paper>
          </Box>

          {/* Summary */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: "#1E8078",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>calculate</Icon>
              Summary
            </Typography>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha("#1E8078", 0.03),
                borderRadius: 3,
                border: `1px solid ${alpha("#1E8078", 0.1)}`,
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                  >
                    Subtotal
                  </Typography>
                  <Typography>BHD {subtotal.toFixed(3)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                  >
                    Tax (10%)
                  </Typography>
                  <Typography>BHD {tax.toFixed(3)}</Typography>
                </Stack>
                <Divider sx={{ borderColor: alpha("#1E8078", 0.1) }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#1E8078" }}
                  >
                    BHD {total.toFixed(3)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Box>

          {/* Payment Details */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: "#1E8078",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>payments</Icon>
              Payment Details
            </Typography>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha("#1E8078", 0.03),
                borderRadius: 3,
                border: `1px solid ${alpha("#1E8078", 0.1)}`,
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                  >
                    Payment Method
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: alpha("#1E8078", 0.1),
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    <Icon sx={{ color: "#1E8078", fontSize: 20 }}>
                      {paymentMethod === "cash" ? "payments" : "credit_card"}
                    </Icon>
                    <Typography
                      sx={{
                        color: "#1E8078",
                        fontWeight: 500,
                        textTransform: "capitalize",
                      }}
                    >
                      {paymentMethod}
                    </Typography>
                  </Box>
                </Stack>
                {amountPaid && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                    >
                      Amount Paid
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      BHD {amountPaid.toFixed(3)}
                    </Typography>
                  </Stack>
                )}
                {change && change > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{ color: alpha(theme.palette.text.primary, 0.6) }}
                    >
                      Change Due
                    </Typography>
                    <Typography sx={{ color: "#1E8078", fontWeight: 600 }}>
                      BHD {change.toFixed(3)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>

      <Box
        sx={{
          p: 3,
          backgroundColor: alpha(theme.palette.background.paper, 0.5),
          borderTop: `1px solid ${alpha("#1E8078", 0.1)}`,
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: alpha("#1E8078", 0.3),
              color: "#1E8078",
              "&:hover": {
                borderColor: "#1E8078",
                backgroundColor: alpha("#1E8078", 0.05),
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={onPrint}
            variant="contained"
            startIcon={<Icon>print</Icon>}
            sx={{
              backgroundColor: "#1E8078",
              "&:hover": {
                backgroundColor: "#166F68",
              },
            }}
          >
            Print Receipt
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};
